import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, UserPlus, RefreshCw, CheckCircle2, Archive, Trash2 } from "lucide-react";
import UserProfileLink from "@/components/UserProfileLink";
import Pager, { usePager } from "@/components/Pager";

const STATUS_BADGE = {
  submitted: "border-amber-200 bg-amber-50 text-amber-700",
  priced: "border-sky-200 bg-sky-50 text-sky-700",
  paid: "border-indigo-200 bg-indigo-50 text-indigo-700",
  assigned: "border-emerald-200 bg-emerald-50 text-emerald-700",
  in_progress: "border-emerald-200 bg-emerald-50 text-emerald-700",
  completed: "border-emerald-300 bg-emerald-100 text-emerald-700",
  archived: "border-slate-200 bg-slate-50 text-slate-500",
};

export default function AdminGuidancePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [pricing, setPricing] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [busy, setBusy] = useState({});
  const pager = usePager(items, 15);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/guidance", { params: includeArchived ? { include_archived: true } : {} });
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const setPrice = async (id) => {
    const price_npr = Number(pricing[id]?.price);
    if (!price_npr || price_npr < 100) { toast.error("Enter a price ≥ NPR 100"); return; }
    try {
      await api.post(`/admin/guidance/${id}/price`, { price_npr, admin_notes: pricing[id]?.note || "" });
      toast.success("Priced — client can pay now");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const fetchSuggestions = async (id) => {
    setBusy((b) => ({ ...b, [`s_${id}`]: true }));
    try {
      const r = await api.get(`/admin/guidance/${id}/suggest-mentors`);
      setSuggestions((s) => ({ ...s, [id]: r.data }));
    } catch (e) { toast.error("Couldn't load suggestions"); }
    finally { setBusy((b) => ({ ...b, [`s_${id}`]: false })); }
  };

  const assignMentor = async (id, mentor_id) => {
    setBusy((b) => ({ ...b, [`${id}_${mentor_id}`]: true }));
    try {
      await api.post(`/admin/guidance/${id}/assign-mentor`, { mentor_id });
      toast.success("Mentor assigned — workspace unlocks for both sides");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [`${id}_${mentor_id}`]: false })); }
  };

  const recommendMentor = async (id, mentor_id) => {
    // Used when the client didn't pick anyone (status = awaiting_admin).
    // Auto-quotes against the picked mentor's hourly rate on the backend.
    try {
      const r = await api.post(`/admin/guidance/${id}/recommend-mentor`, { mentor_id, note: "" });
      toast.success(r.data?.price_npr != null ? `Recommended & auto-quoted NPR ${r.data.price_npr}` : "Mentor recommended — set price manually below");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addCoMentor = async (id, mentor_id) => {
    // Attach an ADDITIONAL mentor to an already-assigned workspace. Backend
    // dedupes by mentor_id so re-clicking is a no-op.
    setBusy((b) => ({ ...b, [`co_${id}_${mentor_id}`]: true }));
    try {
      await api.post(`/guidance/${id}/mentors`, { mentor_id });
      toast.success("Co-mentor added — they can now see the workspace");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [`co_${id}_${mentor_id}`]: false })); }
  };
  const removeCoMentor = async (id, mentor_id) => {
    if (!window.confirm("Remove this co-mentor from the workspace?")) return;
    try {
      await api.delete(`/guidance/${id}/mentors/${mentor_id}`);
      toast.success("Co-mentor removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markCompleted = async (id) => {
    if (!window.confirm("Mark this guidance as completed?")) return;
    try {
      await api.post(`/guidance/${id}/mark-completed`);
      toast.success("Marked completed");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this completed guidance? It will be hidden from the default list.")) return;
    try {
      await api.post(`/admin/guidance/${id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteGuidance = async (g) => {
    if (!window.confirm(
      `Permanently delete this guidance request?\n\n"${(g.problem_description || "").slice(0, 100)}"\n\n` +
      "All messages, files, deliverables and tasks tied to its workspace will be removed. This cannot be undone."
    )) return;
    setBusy((b) => ({ ...b, [`del_${g.id}`]: true }));
    try {
      await api.delete(`/guidance/${g.id}`);
      toast.success("Deleted");
      setItems((arr) => arr.filter((x) => x.id !== g.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusy((b) => ({ ...b, [`del_${g.id}`]: false }));
    }
  };

  return (
    <div data-testid="admin-guidance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">GUIDANCE REQUESTS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Price, route & <em className="italic text-kb-primary">close out</em></h1>
        </div>
        <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-2">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="guidance-show-archived" /> SHOW ARCHIVED
        </label>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No requests.</div> :
        <div className="mt-8 space-y-4">
          {pager.pageItems.map((g) => (
            <div key={g.id} className="kb-card p-6" data-testid={`admin-guidance-${g.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                    {g.id} · {g.type} · by <UserProfileLink userId={g.user_id} name={g.user_name} />
                  </div>
                  {g.domain && <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mt-1">DOMAIN: {g.domain}</div>}
                  <p className="text-kb-ink mt-2 line-clamp-3">{g.problem_description}</p>
                  <div className="kb-mono text-[10px] text-kb-ink-light mt-2">
                    Est. {g.estimated_hours}h · {new Date(g.created_at).toLocaleDateString()}
                    {g.mentor_name && <> · Mentor: <UserProfileLink userId={g.mentor_user_id} name={g.mentor_name} /></>}
                  </div>
                </div>
                <div className="flex items-start gap-2 shrink-0">
                  <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${STATUS_BADGE[g.status] || "border-slate-200"}`}>{g.status}</span>
                  <button
                    onClick={() => deleteGuidance(g)}
                    disabled={busy[`del_${g.id}`]}
                    title="Delete request"
                    className="p-1.5 text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-200 hover:border-rose-600 rounded-sm transition-colors disabled:opacity-50"
                    data-testid={`admin-guidance-${g.id}-delete-btn`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* AWAITING_ADMIN — client didn't pick a mentor, admin recommends one */}
              {g.status === "awaiting_admin" && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                      CLIENT AWAITING MENTOR RECOMMENDATION
                    </div>
                    <button onClick={() => fetchSuggestions(g.id)} disabled={busy[`s_${g.id}`]}
                      className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1.5">
                      <RefreshCw className={`w-3 h-3 ${busy[`s_${g.id}`] ? "animate-spin" : ""}`} />
                      Suggest mentors
                    </button>
                  </div>
                  {suggestions[g.id] && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {suggestions[g.id].length === 0 && (
                        <div className="col-span-full text-sm text-kb-ink-muted">No matching mentors — try refining the domain.</div>
                      )}
                      {suggestions[g.id].map((m, i) => (
                        <div key={m.id} className="border border-slate-200 rounded-sm p-3">
                          <div className="flex items-center gap-2">
                            <img src={m.photo_url} alt="" className="w-8 h-8 rounded-full" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-kb-ink truncate">{m.name}</div>
                              <div className="kb-mono text-[10px] text-kb-ink-light truncate">{m.affiliation}{m.hourly_rate_npr ? ` · NPR ${m.hourly_rate_npr}/hr` : " · rate TBD"}</div>
                            </div>
                            {i === 0 && <span className="kb-mono text-[9px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">TOP</span>}
                          </div>
                          <div className="mt-2 kb-mono text-[10px] text-kb-ink-muted">match {m.match_score} · h-index {m.h_index}</div>
                          <button onClick={() => recommendMentor(g.id, m.id)}
                            className="kb-btn-primary text-xs w-full mt-3 justify-center" data-testid={`guidance-${g.id}-recommend-${m.id}`}>
                            Recommend {m.hourly_rate_npr ? `& auto-quote NPR ${(m.hourly_rate_npr * (g.estimated_hours || 0)).toLocaleString()}` : ""}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* QUOTED — auto-priced; awaiting slot agreement */}
              {g.status === "quoted" && (
                <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-kb-ink-muted">
                  Auto-quoted <strong className="text-emerald-700">NPR {(g.price_npr || 0).toLocaleString()}</strong>. Waiting for client &amp; mentor to lock a slot before payment unlocks.
                </div>
              )}

              {/* SCHEDULING — slot negotiation in progress */}
              {g.status === "scheduling" && (
                <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-kb-ink-muted">
                  Slot negotiation in progress. {(g.proposed_slots || []).length} client slots · {(g.counter_slots || []).length} mentor counters.
                </div>
              )}

              {/* SUBMITTED — set price */}
              {g.status === "submitted" && (
                <div className="mt-4 pt-4 border-t border-slate-200 grid sm:grid-cols-12 gap-2">
                  <input type="number" placeholder="Price NPR" className="kb-input sm:col-span-3"
                    value={pricing[g.id]?.price || ""}
                    onChange={(e) => setPricing({ ...pricing, [g.id]: { ...(pricing[g.id] || {}), price: e.target.value } })}
                    data-testid={`guidance-${g.id}-price-input`} />
                  <input placeholder="Admin note" className="kb-input sm:col-span-7"
                    value={pricing[g.id]?.note || ""}
                    onChange={(e) => setPricing({ ...pricing, [g.id]: { ...(pricing[g.id] || {}), note: e.target.value } })} />
                  <button onClick={() => setPrice(g.id)} className="kb-btn-primary text-sm sm:col-span-2" data-testid={`guidance-${g.id}-price-btn`}>
                    <CreditCard className="w-3.5 h-3.5" /> Set price
                  </button>
                </div>
              )}

              {/* PAID — suggest + assign mentor */}
              {g.status === "paid" && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                      {suggestions[g.id] ? "SUGGESTED MENTORS" : "FIND THE RIGHT MENTOR"}
                    </div>
                    <button onClick={() => fetchSuggestions(g.id)} disabled={busy[`s_${g.id}`]}
                      className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1.5"
                      data-testid={`guidance-${g.id}-suggest`}>
                      <RefreshCw className={`w-3 h-3 ${busy[`s_${g.id}`] ? "animate-spin" : ""}`} />
                      {suggestions[g.id] ? "Refresh suggestions" : "Suggest mentors"}
                    </button>
                  </div>
                  {suggestions[g.id] && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`guidance-${g.id}-suggestions`}>
                      {suggestions[g.id].length === 0 && (
                        <div className="col-span-full text-sm text-kb-ink-muted">No matching mentors found — try refining the problem description&apos;s domain.</div>
                      )}
                      {suggestions[g.id].map((m, i) => (
                        <div key={m.id} className="border border-slate-200 rounded-sm p-3" data-testid={`suggest-guidance-${g.id}-${m.id}`}>
                          <div className="flex items-center gap-2">
                            <img src={m.photo_url} alt="" className="w-8 h-8 rounded-full" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-kb-ink truncate">{m.name}</div>
                              <div className="kb-mono text-[10px] text-kb-ink-light truncate">{m.affiliation}</div>
                            </div>
                            {i === 0 && <span className="kb-mono text-[9px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">TOP</span>}
                          </div>
                          <div className="mt-2 kb-mono text-[10px] text-kb-ink-muted">match {m.match_score} · h-index {m.h_index} · {m.active_projects} active</div>
                          <button onClick={() => assignMentor(g.id, m.id)} disabled={busy[`${g.id}_${m.id}`]}
                            className="kb-btn-primary text-xs w-full mt-3 justify-center" data-testid={`guidance-${g.id}-assign-${m.id}`}>
                            <UserPlus className="w-3 h-3" /> {busy[`${g.id}_${m.id}`] ? "Assigning…" : "Assign mentor"}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ASSIGNED — show existing mentors + allow additional co-mentors + mark completed */}
              {["assigned", "in_progress"].includes(g.status) && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                  <div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">MENTORS ON THIS REQUEST</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`guidance-${g.id}-mentor-list`}>
                      <span className="kb-mono text-[10px] px-2 py-1 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700">
                        Primary: {g.mentor_name || g.mentor_user_id || "—"}
                      </span>
                      {(g.co_mentors || []).map((cm) => (
                        <span key={cm.mentor_id} className="kb-mono text-[10px] px-2 py-1 rounded-sm border border-indigo-200 bg-indigo-50 text-indigo-700 inline-flex items-center gap-1" data-testid={`guidance-${g.id}-comentor-${cm.mentor_id}`}>
                          + {cm.name || cm.mentor_id}
                          <button type="button" onClick={() => removeCoMentor(g.id, cm.mentor_id)} className="text-rose-500 hover:text-rose-700" title="Remove">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                        {suggestions[g.id] ? "SUGGESTED CO-MENTORS" : "ADD ANOTHER MENTOR"}
                      </div>
                      <button onClick={() => fetchSuggestions(g.id)} disabled={busy[`s_${g.id}`]}
                        className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1.5"
                        data-testid={`guidance-${g.id}-suggest-co`}>
                        <RefreshCw className={`w-3 h-3 ${busy[`s_${g.id}`] ? "animate-spin" : ""}`} />
                        {suggestions[g.id] ? "Refresh" : "Suggest mentors"}
                      </button>
                    </div>
                    {suggestions[g.id] && (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2" data-testid={`guidance-${g.id}-co-suggestions`}>
                        {suggestions[g.id].filter((m) => m.id !== g.mentor_id && !(g.co_mentors || []).some((c) => c.mentor_id === m.id)).map((m) => (
                          <div key={m.id} className="border border-slate-200 rounded-sm p-3">
                            <div className="flex items-center gap-2">
                              <img src={m.photo_url} alt="" className="w-8 h-8 rounded-full" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-kb-ink truncate">{m.name}</div>
                                <div className="kb-mono text-[10px] text-kb-ink-light truncate">{m.affiliation}</div>
                              </div>
                            </div>
                            <div className="mt-2 kb-mono text-[10px] text-kb-ink-muted">match {m.match_score} · h-index {m.h_index}</div>
                            <button onClick={() => addCoMentor(g.id, m.id)} disabled={busy[`co_${g.id}_${m.id}`]}
                              className="kb-btn-outline text-xs w-full mt-3 justify-center" data-testid={`guidance-${g.id}-add-co-${m.id}`}>
                              <UserPlus className="w-3 h-3" /> {busy[`co_${g.id}_${m.id}`] ? "Adding…" : "Add as co-mentor"}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => markCompleted(g.id)} className="kb-btn-primary text-sm" data-testid={`guidance-${g.id}-complete`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Mark completed
                    </button>
                  </div>
                </div>
              )}

              {/* COMPLETED — archive */}
              {g.status === "completed" && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                  <button onClick={() => archive(g.id)} className="kb-btn-outline text-sm" data-testid={`guidance-${g.id}-archive`}>
                    <Archive className="w-3.5 h-3.5" /> Archive
                  </button>
                </div>
              )}

              {g.price_npr > 0 && <div className="mt-3 kb-mono text-xs text-kb-primary">Priced at NPR {g.price_npr.toLocaleString()}</div>}
            </div>
          ))}
          <Pager {...pager} label="requests" testId="admin-guidance-pager" />
        </div>
      }
    </div>
  );
}
